import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Pencil, Landmark, KeyRound } from "lucide-react";
import { ResetPasswordDialog } from "@/components/ResetPasswordDialog";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Pager } from "@/components/Pager";
import { BorrowerDialog } from "@/components/BorrowerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const PAGE_SIZE = 20;

export default function Borrowers() {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [dlg, setDlg] = useState({ open: false, borrower: null });
  const [tick, setTick] = useState(0);
  const [resetting, setResetting] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setQ(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    api.get("/borrowers", { params: { search: q, page, page_size: PAGE_SIZE } }).then((r) => setData(r.data));
  }, [q, page, tick]);

  return (
    <div data-testid="borrowers-page">
      <PageHeader eyebrow="Relationships" title="Borrowers" subtitle={`${data.total} borrower${data.total === 1 ? "" : "s"}`}
        actions={<Button onClick={() => setDlg({ open: true, borrower: null })} data-testid="new-borrower-button"><Plus /> New borrower</Button>} />

      <div className="relative mb-5 w-full md:w-80">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email" className="h-10 bg-card pl-9" data-testid="borrower-search-input" />
      </div>

      <div className="fade-up overflow-x-auto rounded-lg border bg-card">
        <Table data-testid="borrowers-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {["Name", "Email", "Phone", "Loans", "Principal balance", ""].map((h, i) => <TableHead key={i} className="eyebrow h-11 whitespace-nowrap">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((b) => (
              <TableRow key={b.id} data-testid={`borrower-row-${b.email}`}>
                <TableCell>
                  <div className="font-medium">{b.name}</div>
                  {b.address && <div className="max-w-[260px] truncate text-xs text-muted-foreground">{b.address}</div>}
                </TableCell>
                <TableCell className="text-sm">{b.email}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{b.phone || "—"}</TableCell>
                <TableCell className="num">{b.loan_count}</TableCell>
                <TableCell className="num font-medium">{money(b.balance_cents)}</TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="sm" asChild data-testid={`borrower-loans-${b.email}`}>
                    <Link to={`/admin/loans?borrower_id=${b.id}&borrower_name=${encodeURIComponent(b.name)}`}><Landmark /> Loans</Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild data-testid={`borrower-new-loan-${b.email}`}>
                    <Link to={`/admin/loans/new?borrower=${b.id}`}><Plus /> Loan</Link>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDlg({ open: true, borrower: b })} data-testid={`borrower-edit-${b.email}`}><Pencil /> Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => setResetting(b)} data-testid={`borrower-reset-password-${b.email}`}><KeyRound /> Reset password</Button>
                </TableCell>
              </TableRow>
            ))}
            {!data.items.length && (
              <TableRow><TableCell colSpan={6} className="py-14 text-center text-sm text-muted-foreground" data-testid="borrowers-empty">No borrowers found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} testid="borrowers-pager" />
      <ResetPasswordDialog borrower={resetting} onOpenChange={(o) => !o && setResetting(null)} />
      <BorrowerDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} borrower={dlg.borrower} onSaved={() => setTick((t) => t + 1)} />
    </div>
  );
}
